<template>
  <div>
    <div :class="$style.reportContainer">
      <div :class="$style.topContainer">
        <ProjectReportFilters
          v-if="showProjectReportFilters"
          :projectSites="projectSiteList"
          :projectCriteria="projectCriteriaList"
          :analysisFilter="analysisFilter"
          :auditsConfigurationList="getAuditConfigurationList"
          :class="[
            $style.filterContainer,
            isLoading ? $style.adjustMarginBottom : null,
          ]"
          :reportLoading="isLoading"
          @applyAnalysisFilters="applyAnalysisFilters"
        />
        <div :class="$style.analysisActionsContainer">
          <b-dropdown aria-role="list" class="analysisActionsDropdown">
            <button slot="trigger">
              <b-icon icon="dots-vertical"></b-icon>
            </button>
            <b-dropdown-item aria-role="listitem" @click="renameAnalysis"
              >Rename</b-dropdown-item
            >
            <b-dropdown-item aria-role="listitem" @click="deleteAnalysis"
              >Delete</b-dropdown-item
            >
          </b-dropdown>
        </div>
      </div>
      <SingleSiteReport
        v-if="getAllSiteIds.length === 1 && showSingleSiteReport"
        :analysisFilter="analysisFilter"
        :projectSitelist="projectSitelist"
        :projectCriteriaList="projectCriteriaList"
        :reportResponse="projectReportResponse"
        :class="$style.singleSiteReportContainer"
      >
      </SingleSiteReport>
      <SingleSiteComparisonReport
        v-if="getAllSiteIds.length === 1 && showSingleSiteComparisonReport"
        :analysisFilter="analysisFilter"
        :projectCriteriaList="projectCriteriaList"
        :projectSitelist="projectSitelist"
        :comparisonReportResponse="projectComparisonReportResponse"
        :class="$style.singleSiteComparisonReportContainer"
      >
      </SingleSiteComparisonReport>
      <MultiSiteReport
        v-if="getAllSiteIds.length > 1 && showMultiSiteReport"
        :analysisFilter="analysisFilter"
        :projectSitelist="projectSitelist"
        :projectCriteriaList="projectCriteriaList"
        :reportResponse="projectReportResponse"
        :class="$style.multiSiteReportContainer"
      >
      </MultiSiteReport>
      <MultiSiteComparisonReport
        v-if="getAllSiteIds.length > 1 && showMultiSiteComparisonReport"
        :analysisFilter="analysisFilter"
        :projectCriteriaList="projectCriteriaList"
        :projectSitelist="projectSitelist"
        :comparisonReportResponse="projectComparisonReportResponse"
        :class="$style.singleSiteComparisonReportContainer"
      >
      </MultiSiteComparisonReport>
      <div v-if="isLoading" :class="$style.loadingContainer">
        <svg :class="$style.spinner" viewBox="0 0 50 50">
          <circle
            :class="$style.path"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke-width="5"
          ></circle>
        </svg>
      </div>
      <div
        v-if="validateSiteCriteriaFilter"
        :class="$style.validateFilterContainer"
      >
        No site or criteria selected.
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { cloneDeep as _cloneDeep, map as _map, get as _get } from 'lodash';
import { Action, State } from 'vuex-class';
import ProjectReportFilters from '@/views/Project/components/ProjectReportFilters.vue';
import RenameAnalysisModal from '@/components/reports/RenameAnalysisModal.vue';
import DeleteAnalysisModal from '@/components/reports/DeleteAnalysisModal.vue';
import SingleSiteReport from '@/views/Project/components/SingleSiteReport.vue';
import SingleSiteComparisonReport from '@/views/Project/components/SingleSiteComparisonReport.vue';
import MultiSiteReport from '@/views/Project/components/MultiSiteReport.vue';
import MultiSiteComparisonReport from '@/views/Project/components/MultiSiteComparisonReport.vue';
import {
  ChartType,
  ProjectCriteria,
  ProjectSite,
  DeleteAnalysisPayload,
  UpdateAnalysisTitlePayload,
  ProjectReportAnalysis,
  ProjectReportPeriod,
  ProjectReportFiltersConfig,
  ProjectReportSinglePeriodResponse,
  ProjectReportMultiplePeriodResponse,
  ProjectReportAnalysisPayload,
} from '@/store/modules/projects/types/projects.types';
import { RootState } from '../../../store/store';
import { ApiState } from '../../../store/types/general.types';
import { isTruthy } from '../../../jbi-shared/util/watcher.vue-decorator';

@Component({
  components: {
    ProjectReportFilters,
    RenameAnalysisModal,
    DeleteAnalysisModal,
    SingleSiteReport,
    SingleSiteComparisonReport,
    MultiSiteReport,
    MultiSiteComparisonReport,
  },
})
export default class ProjectReportIndividualAnalysis extends Vue {
  @Prop()
  public projectId!: number;
  @Prop()
  public projectSites!: ProjectSite[];
  @Prop()
  public projectCriteria!: ProjectCriteria[];
  @Prop()
  public analysisDetail!: ProjectReportAnalysis;
  @Prop()
  public auditsConfigurationList!: ProjectReportPeriod[];

  public analysisFilter: ProjectReportFiltersConfig = {
    filteredSites: [],
    filteredCriterias: [],
    selectedChartType: '',
    checkIndividualSite: false,
    checkIndividualCriteria: false,
    selectedPeriod: {
      title: '',
      startedAt: '',
      endedAt: null,
      isAudit: false,
      auditIds: [],
    },
    checkComparison: false,
    comparisonPeriods: [],
  };
  public showProjectReportFilters: boolean = false;
  public dirtyAnalysisDetails: any = {};
  public isLoading: boolean = false;
  public validateSiteCriteriaFilter: boolean = false;
  public showSingleSiteReport: boolean = false;
  public showMultiSiteReport: boolean = false;
  public showSingleSiteComparisonReport: boolean = false;
  public showMultiSiteComparisonReport: boolean = false;
  public projectReportResponse: ProjectReportSinglePeriodResponse = {
    chartData: [],
    statisticData: {
      configurationData: null,
      samplingConfiguration: null,
    },
    success: false,
    analysisId: 0,
  };
  public projectComparisonReportResponse: ProjectReportMultiplePeriodResponse = {
    comparisonResult: [],
    success: false,
    analysisId: 0,
  };

  @Action('projects/getProjectReportAnalysis')
  public getProjectReportAnalysis!: (
    payload: ProjectReportAnalysisPayload,
  ) => void;

  @State((state: RootState) => state.projects.projectReportAnalysis)
  public projectReportAnalysis!: any;

  @State((state: RootState) => state.projects.apiState.getProjectReportAnalysis)
  public getProjectReportAnalysisState!: ApiState;

  @Action('projects/updateProjectReportAnalysisTitleById')
  public updateProjectReportAnalysis!: (
    payload: UpdateAnalysisTitlePayload,
  ) => void;

  @State((state: RootState) => state.projects.updatedProjectReportAnalysis)
  public updatedProjectReportAnalysis!: any;

  @State(
    (state: RootState) => state.projects.apiState.updateProjectReportAnalysis,
  )
  public updateProjectReportAnalysisState!: ApiState;

  @Action('projects/deleteProjectReportAnalysisById')
  public deleteProjectReportAnalysis!: (payload: DeleteAnalysisPayload) => void;

  @State(
    (state: RootState) => state.projects.apiState.deleteProjectReportAnalysis,
  )
  public deleteProjectReportAnalysisState!: ApiState;

  public mounted() {
    this.dirtyAnalysisDetails = _cloneDeep(this.analysisDetail);
    this.analysisFilter = _cloneDeep(
      this.dirtyAnalysisDetails.filtersConfiguration,
    );
    this.showProjectReportFilters = true;
    const payload: ProjectReportAnalysisPayload = {
      ...this.analysisFilter,
      projectId: this.getProjectId,
      analysisId: this.getAnalysisId,
      analysisTitle: this.getAnalysisTitle,
      checkIndividualSite:
        this.analysisFilter.filteredSites.length > 1
          ? this.analysisFilter.checkIndividualSite
          : false,
      type: this.projectSitelist.length > 1 ? 'multiSite' : 'singleSite',
    };
    if (this.$route.hash === '#report') {
      this.getProjectReportAnalysisData(payload);
    }
  }

  public applyAnalysisFilters(newValue: ProjectReportFiltersConfig) {
    this.analysisFilter = _cloneDeep(newValue);
    this.$emit('updateAnalysisFilter', this.analysisFilter);
    if (
      this.analysisFilter.filteredSites.length === 0 ||
      this.analysisFilter.filteredCriterias.length === 0
    ) {
      this.showSingleSiteReport = false;
      this.showSingleSiteComparisonReport = false;
      this.showMultiSiteReport = false;
      this.showMultiSiteComparisonReport = false;
      this.validateSiteCriteriaFilter = true;
    } else {
      const payload: ProjectReportAnalysisPayload = {
        ...this.analysisFilter,
        projectId: this.getProjectId,
        analysisId: this.getAnalysisId,
        analysisTitle: this.getAnalysisTitle,
        checkIndividualSite:
          this.analysisFilter.filteredSites.length > 1
            ? this.analysisFilter.checkIndividualSite
            : false,
        type: this.projectSitelist.length > 1 ? 'multiSite' : 'singleSite',
      };
      if (this.projectSitelist.length === 1) {
        this.showSingleSiteReport = false;
        this.showSingleSiteComparisonReport = false;
      } else {
        this.showMultiSiteReport = false;
        this.showMultiSiteComparisonReport = false;
      }
      this.isLoading = true;
      this.validateSiteCriteriaFilter = false;
      this.getProjectReportAnalysisData(payload);
    }
  }

  public getProjectReportAnalysisData(payload: ProjectReportAnalysisPayload) {
    this.getProjectReportAnalysis(payload);
  }

  @Watch('getProjectReportAnalysisState.success')
  @isTruthy
  public watchGetProjectReportAnalysisState(): void {
    if (this.analysisFilter.checkComparison) {
      this.projectComparisonReportResponse = JSON.parse(
        JSON.stringify(this.projectReportAnalysis),
      );
    } else {
      this.projectReportResponse = JSON.parse(
        JSON.stringify(this.projectReportAnalysis),
      );
    }
    if (
      this.dirtyAnalysisDetails.analysisId !==
      JSON.parse(JSON.stringify(this.projectReportAnalysis)).analysisId
    ) {
      this.dirtyAnalysisDetails.analysisId = JSON.parse(
        JSON.stringify(this.projectReportAnalysis),
      ).analysisId;
      this.$emit('updateAnalysisId', this.dirtyAnalysisDetails.analysisId);
    }
  }

  @Watch('updateProjectReportAnalysisState.success')
  @isTruthy
  public watchUpdateProjectReportAnalysisState(): void {
    this.$emit('updateAnalysisTitle', this.updatedProjectReportAnalysis.title);
  }

  @Watch('deleteProjectReportAnalysisState.success')
  @isTruthy
  public watchDeleteProjectReportAnalysisState(): void {
    this.$emit('deleteAnalysis');
  }

  @Watch('projectReportResponse')
  public watchProjectReportResponse(
    newValue: ProjectReportSinglePeriodResponse,
  ) {
    if (this.projectSitelist.length === 1) {
      if (newValue.success) {
        this.showSingleSiteReport = true;
        this.isLoading = false;
      }
    } else {
      if (newValue.success) {
        this.showMultiSiteReport = true;
        this.isLoading = false;
      }
    }
  }

  @Watch('projectComparisonReportResponse')
  public watchProjectComparisonResponse(
    newValue: ProjectReportMultiplePeriodResponse,
  ) {
    if (this.projectSitelist.length === 1) {
      if (newValue.success) {
        this.showSingleSiteComparisonReport = true;
        this.isLoading = false;
      }
    } else {
      if (newValue.success) {
        this.showMultiSiteComparisonReport = true;
        this.isLoading = false;
      }
    }
  }

  public renameAnalysis() {
    this.$buefy.modal.open({
      parent: this,
      component: RenameAnalysisModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        currentAnalysisTitle: this.dirtyAnalysisDetails.title,
      },
      events: {
        updateAnalysisTitle: (newAnalysisTitle: string) => {
          this.updateAnalysisTitleById(newAnalysisTitle);
        },
      },
    });
  }

  public updateAnalysisTitleById(newAnalysisTitle: string) {
    const payload: UpdateAnalysisTitlePayload = {
      projectId: this.getProjectId,
      analysisId: this.dirtyAnalysisDetails.analysisId,
      analysisTitle: newAnalysisTitle,
    };
    this.updateProjectReportAnalysis(payload);
  }

  public deleteAnalysis() {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteAnalysisModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        analysisTitle: this.dirtyAnalysisDetails.title,
      },
      events: {
        deleteAnalysis: () => {
          this.deleteAnalysisById();
        },
      },
    });
  }

  public deleteAnalysisById() {
    const payload: DeleteAnalysisPayload = {
      projectId: this.getProjectId,
      analysisId: this.dirtyAnalysisDetails.analysisId,
    };
    this.deleteProjectReportAnalysis(payload);
  }

  get projectSiteList(): ProjectSite[] {
    return this.projectSites;
  }

  get projectCriteriaList(): ProjectCriteria[] {
    return this.projectCriteria;
  }

  get getProjectId(): number {
    return this.projectId;
  }

  get getAnalysisId(): number {
    return this.dirtyAnalysisDetails.analysisId;
  }

  get getAnalysisTitle(): string {
    return this.dirtyAnalysisDetails.title;
  }

  get projectSitelist(): ProjectSite[] {
    return this.projectSites;
  }

  get getAllSiteIds(): number[] {
    const siteIdList: number[] = [];
    _map(this.projectSites, (projectSite: ProjectSite) => {
      siteIdList.push(projectSite.id);
    });
    return siteIdList;
  }

  get getAuditConfigurationList(): ProjectReportPeriod[] {
    return this.auditsConfigurationList;
  }
}
</script>
<style lang="scss" module>
.reportContainer {
  background: white;
  min-height: unset;
  padding: 0px 0px 0px 0px;
  flex-direction: column;
  margin-top: 23px;
  margin-bottom: 0px;
}
.filterContainer {
  width: 90%;
  margin: 0px 24px 24px 24px;
}
.singleSiteReportContainer,
.singleSiteComparisonReportContainer,
.multiSiteReportContainer,
.multiSiteComparisonReportContainer {
  padding-top: 16px;
  padding-bottom: 0px;
}
.loadingContainer {
  .spinner {
    animation: rotate 2s linear infinite;
    z-index: 2;
    position: absolute;
    left: 50%;
    top: 80%;
    margin: -25px 0 0 -25px;
    width: 50px;
    height: 50px;
    & .path {
      stroke: #00205b;
      stroke-linecap: round;
      animation: dash 1.5s ease-in-out infinite;
    }
    @keyframes rotate {
      100% {
        transform: rotate(360deg);
      }
    }
    @keyframes dash {
      0% {
        stroke-dasharray: 1, 150;
        stroke-dashoffset: 0;
      }
      50% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -35;
      }
      100% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -124;
      }
    }
  }
}
.adjustMarginBottom {
  margin-bottom: 324px;
}
.validateFilterContainer {
  margin-top: 125px;
  margin-bottom: 125px;
  text-align: center;
  color: #9f9f9f;
  font-weight: bold;
  font-size: 18px;
}
.topContainer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.analysisActionsContainer {
  text-align: right;
  width: 10%;
  margin-top: 0px;
  margin-right: 24px;
}
.analysisSaveButton {
  margin-right: 16px;
  background-color: #00205b;
  color: white;
  border-radius: 24px;
  border: unset;
  width: 82px;
  height: 44px;
  font-size: 14px;
  font-weight: bold;
}
</style>
